import type { GoalSnapshotResponse, ProductCostsSource, SnapshotType } from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as portfolioSnapshotsApi from "@/lib/api/flow-instances/portfolio-snapshots";

// ─── Query keys ──────────────────────────────────────────────────────────────

export const portfolioSnapshotQueryKeys = {
  portfolio: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "portfolio"] as const,
  xray: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "xray"] as const,
  assetAllocation: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "assetAllocation"] as const,
  historicalReturn: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "historicalReturn"] as const,
  forecast: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "forecast"] as const,
  cost: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "cost"] as const,
  expectedValue: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "expectedValue"] as const,
  sustainability: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "sustainability"] as const,
  /** QL-417 — nested under "snapshot" so allForPortfolio invalidates it too. */
  productCosts: (portfolioId: string | null, source: ProductCostsSource, language: string) =>
    [
      "flowInstances",
      "portfolios",
      portfolioId,
      "snapshot",
      "productCosts",
      source,
      language,
    ] as const,
  /** Every source and language of one portfolio's product costs. */
  productCostsAll: (portfolioId: string) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "productCosts"] as const,
  /** QL-416 — per-portfolio cost/benefit comparison. Same key shape usePortfolioSnapshot builds. */
  costBenefit: (portfolioId: string | null) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot", "costBenefit"] as const,
  /** Matches all snapshot queries for a portfolio — for bulk invalidation */
  allForPortfolio: (portfolioId: string) =>
    ["flowInstances", "portfolios", portfolioId, "snapshot"] as const,
};

// ─── Query hooks ─────────────────────────────────────────────────────────────

function useSnapshot(
  portfolioId: string | null,
  queryKey: readonly unknown[],
  queryFn: (id: string) => Promise<GoalSnapshotResponse>,
) {
  const { data, error, isLoading } = useQuery({
    queryKey,
    queryFn: () => queryFn(portfolioId!),
    enabled: !!portfolioId,
  });
  return { data: data?.data ?? null, generatedAt: data?.generatedAt ?? null, error, isLoading };
}

export function usePortfolioPortfolioSnapshot(portfolioId: string | null) {
  return useSnapshot(
    portfolioId,
    portfolioSnapshotQueryKeys.portfolio(portfolioId),
    portfolioSnapshotsApi.getPortfolioPortfolioSnapshot,
  );
}

export function usePortfolioXraySnapshot(portfolioId: string | null) {
  return useSnapshot(
    portfolioId,
    portfolioSnapshotQueryKeys.xray(portfolioId),
    portfolioSnapshotsApi.getPortfolioXraySnapshot,
  );
}

export function usePortfolioAssetAllocationSnapshot(portfolioId: string | null) {
  return useSnapshot(
    portfolioId,
    portfolioSnapshotQueryKeys.assetAllocation(portfolioId),
    portfolioSnapshotsApi.getPortfolioAssetAllocationSnapshot,
  );
}

export function usePortfolioHistoricalReturnSnapshot(portfolioId: string | null) {
  return useSnapshot(
    portfolioId,
    portfolioSnapshotQueryKeys.historicalReturn(portfolioId),
    portfolioSnapshotsApi.getPortfolioHistoricalReturnSnapshot,
  );
}

export function usePortfolioForecastSnapshot(portfolioId: string | null) {
  return useSnapshot(
    portfolioId,
    portfolioSnapshotQueryKeys.forecast(portfolioId),
    portfolioSnapshotsApi.getPortfolioForecastSnapshot,
  );
}

export function usePortfolioCostSnapshot(portfolioId: string | null) {
  return useSnapshot(
    portfolioId,
    portfolioSnapshotQueryKeys.cost(portfolioId),
    portfolioSnapshotsApi.getPortfolioCostSnapshot,
  );
}

export function usePortfolioExpectedValueSnapshot(portfolioId: string | null) {
  return useSnapshot(
    portfolioId,
    portfolioSnapshotQueryKeys.expectedValue(portfolioId),
    portfolioSnapshotsApi.getPortfolioExpectedValueSnapshot,
  );
}

// ─── Invalidation helper ─────────────────────────────────────────────────────

const SNAPSHOT_TYPE_TO_KEY: Record<
  SnapshotType,
  (portfolioId: string | null) => readonly unknown[]
> = {
  portfolio: portfolioSnapshotQueryKeys.portfolio,
  // QL-707 — never emitted in refreshHints; entry exists only for Record exhaustiveness.
  advisedPortfolio: () => ["flowInstances", "snapshot", "advisedPortfolio"] as const,
  xray: portfolioSnapshotQueryKeys.xray,
  assetAllocation: portfolioSnapshotQueryKeys.assetAllocation,
  historicalReturn: portfolioSnapshotQueryKeys.historicalReturn,
  forecast: portfolioSnapshotQueryKeys.forecast,
  cost: portfolioSnapshotQueryKeys.cost,
  expectedValue: portfolioSnapshotQueryKeys.expectedValue,
  sustainability: portfolioSnapshotQueryKeys.sustainability,
  costBenefit: portfolioSnapshotQueryKeys.costBenefit,
  // Instance-global (QP-224); the server never emits it in refreshHints — the
  // entry exists only for Record exhaustiveness.
  clientPreferences: () => ["flowInstances", "snapshot", "clientPreferences"] as const,
  // QL-315 — the asset-class forecast rows are goal-scoped and instance-scoped,
  // so no key derived from a portfolioId can reach them. The predicate helper
  // below does the invalidation; this entry exists only for Record
  // exhaustiveness.
  assetClassForecast: () => ["flowInstances", "snapshot", "assetClassForecast"] as const,
  // QL-375 — goal-scoped; reached by the predicate helper below, entry for exhaustiveness.
  mandate: () => ["flowInstances", "snapshot", "mandate"] as const,
};

/**
 * QL-315 — invalidate both scopes of the asset-class forecast.
 *
 * A portfolio calculation changes the mix its goal charts and the mix the whole
 * instance charts, but this helper only knows the portfolioId. Matching every
 * asset-class-forecast key in the `flowInstances` tree covers both.
 */
function invalidateAssetClassForecast(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: ({ queryKey: k }) =>
      k[0] === "flowInstances" &&
      ((k[3] === "snapshot" && k[4] === "assetClassForecast") ||
        (k[2] === "snapshot" && k[3] === "instanceAssetClassForecast")),
  });
}

/**
 * QL-314 — invalidate all volatility chart queries.
 *
 * The volatility and equity share charts read the asset_class_forecast
 * snapshot server-side, so they must refetch whenever that snapshot is
 * regenerated. Keyed under the goal subtree, so a predicate is used since this
 * helper only knows the portfolioId.
 */
function invalidateGoalComplianceCharts(queryClient: ReturnType<typeof useQueryClient>) {
  // QL-313 — the equity share chart reads the same snapshot, same predicate.
  queryClient.invalidateQueries({
    predicate: ({ queryKey: k }) =>
      k[0] === "flowInstances" &&
      k[3] === "snapshot" &&
      // QL-375 — the mandate check too: amount edits move its dots.
      (k[4] === "volatilityChart" || k[4] === "equityShareChart" || k[4] === "mandate"),
  });
}

/**
 * QL-675 — invalidate every portfolio's snapshot views, for a whole-instance
 * rebuild: it rewrites each portfolio's rows, and the goal-keyed loops never
 * reach these portfolio-keyed queries (QP-150). Only mounted queries refetch,
 * so matching every portfolio rather than the instance's own costs nothing.
 */
export function useInvalidateAllPortfolioSnapshots() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      predicate: ({ queryKey: k }) =>
        k[0] === "flowInstances" && k[1] === "portfolios" && k[3] === "snapshot",
    });
}

export function useInvalidatePortfolioSnapshots() {
  const queryClient = useQueryClient();
  return (
    portfolioId: string,
    refreshHints?: { invalidated?: SnapshotType[]; cleared?: boolean },
  ) => {
    if (!refreshHints) return;
    if (refreshHints.cleared) {
      queryClient.invalidateQueries({
        queryKey: portfolioSnapshotQueryKeys.allForPortfolio(portfolioId),
      });
      invalidateAssetClassForecast(queryClient);
      invalidateGoalComplianceCharts(queryClient);
    } else if (refreshHints.invalidated?.length) {
      // QL-417 — product costs read the live composition, not a stored
      // snapshot type, so a composition regen ("portfolio") refreshes them.
      if (refreshHints.invalidated.includes("portfolio")) {
        queryClient.invalidateQueries({
          queryKey: portfolioSnapshotQueryKeys.productCostsAll(portfolioId),
        });
      }
      for (const type of refreshHints.invalidated) {
        if (type === "assetClassForecast") {
          invalidateAssetClassForecast(queryClient);
          // QL-314 / QL-313 — the compliance charts read the asset_class_forecast snapshot server-side.
          invalidateGoalComplianceCharts(queryClient);
          continue;
        }
        const keyFn = SNAPSHOT_TYPE_TO_KEY[type];
        if (keyFn) {
          queryClient.invalidateQueries({ queryKey: keyFn(portfolioId) });
        }
      }
    }
  };
}
